import React, {useCallback, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';

import isArray from 'lodash.isarray';
import {clearSelection} from 'src/components/data-table-selection/services/data-table-selection-slice';
import {HeaderPrefix, LicenseTypeFilter} from 'src/components/filters';
import {useEffectWithDeps} from 'src/hooks/useEffectWithDeps';
import {COLUMNS, HEADERS, SELECTION_KEY} from 'src/modules/profile/user/tabs/assigned-accounts/constants';
import {AccountSortField} from 'src/openapi/admin/models/AccountSortField';
import {SortDirection as SortDirections} from 'src/openapi/admin/models/SortDirection';
import {isDeepEqual} from 'src/utils/isDeepEqual';

import {SortHeaderFilter} from '@pb/kit-component-filters';

import {useGetAccountSearchResultLicense} from '../../hooks/useGetAccountSearchResultLicense';
import {accountSearchResultStateActions, accountSearchResultStateSelector} from '../../slices/account-search-result-state.slice';
import {accountSearchResultResultSelector} from '../../slices/account-search-result.slice';

import {HeaderSkeleton} from './header-skeleton';

import type {SortDirection} from 'src/openapi/admin/models/SortDirection';

type Props = {
    accountIds: number[],
    totalCount: number,
};

const LicenseTypeController = ({accountIds, totalCount}: Props) => {
    const dispatch = useDispatch();

    const cacheKeys = useSelector(accountSearchResultStateSelector);
    const {isLoading, loadingError} = useGetAccountSearchResultLicense({
        accountIds,
        totalCount,
        columnId: COLUMNS.LICENSE_TYPE,
    });

    const [licenseTypes, setLicenseTypes] = useState(cacheKeys.filters.licenseType as string[] | undefined);

    useEffectWithDeps((prev, current) => {
        if (!isDeepEqual({prev, current})) {
            setLicenseTypes(current as string[] | undefined);
        }
    }, cacheKeys.filters.licenseType);

    const sort = useCallback((direction: ('ASC' | 'DESC')) => {
        dispatch(clearSelection(SELECTION_KEY));
        dispatch(accountSearchResultStateActions.setSortCriteria({
            direction: direction as SortDirection,
            field: AccountSortField.LICENSE_TYPE,
        }));
    }, [dispatch]);

    const applyFilter = useCallback(() => {
        const {licenseType: _licenseType, ...otherFilters} = cacheKeys.filters;
        const filters = {
            ...otherFilters,
            ...(isArray(licenseTypes) && licenseTypes?.length) ? {licenseType: licenseTypes} : undefined,
        };

        dispatch(clearSelection(SELECTION_KEY));
        dispatch(accountSearchResultStateActions.setFilters(filters));
    }, [cacheKeys.filters, licenseTypes, dispatch]);

    const clearFilter = useCallback(() => {
        const {licenseType: _, ...filters} = cacheKeys.filters;

        dispatch(clearSelection(SELECTION_KEY));
        dispatch(accountSearchResultStateActions.setFilters(filters));
    }, [cacheKeys.filters, dispatch]);

    const cancelFilter = useCallback(() => {
        setLicenseTypes(cacheKeys.filters.licenseType as string[] | undefined);
    }, [cacheKeys.filters, setLicenseTypes]);

    return (
        <SortHeaderFilter
            filterActive={!!cacheKeys.filters?.licenseType}
            sortable={true}
            contentBefore={(isLoading || loadingError)
                ? <HeaderPrefix isLoading={isLoading} loadingError={loadingError}/>
                : undefined}
            caption={HEADERS[COLUMNS.LICENSE_TYPE]}
            direction={cacheKeys.sortCriteria.field === AccountSortField.LICENSE_TYPE
                ? cacheKeys.sortCriteria.direction
                : undefined}
            defaultDirection={SortDirections.ASC}
            onSort={sort}
            onApply={applyFilter}
            onReset={clearFilter}
            onCancel={cancelFilter}>
            <LicenseTypeFilter selectedLicenseTypes={licenseTypes as string[] | undefined} onChange={setLicenseTypes}/>
        </SortHeaderFilter>
    );
};

export const LicenseTypeHeader = () => {
    const result = useSelector(accountSearchResultResultSelector);

    return result ? (
        <LicenseTypeController
            accountIds={result.data.map(({accountId}) => accountId)}
            totalCount={result.totalCount}
        />
    ) : (
        <HeaderSkeleton columnId={COLUMNS.LICENSE_TYPE}/>
    );
};
